import type { ColumnFiltersState } from '@tanstack/react-table';
import type { AreaGeometriesQueryParams } from './atoms';

/** Page sizes the Areas catalog offers, per the product spec. */
export const PAGE_SIZES = [25, 50, 75, 100];

/**
 * Baseline query payload. `active: true` matches the product default ("an Active
 * checkbox that, on by default, hides inactive areas"); every other filter starts
 * unset so the catalog opens unfiltered.
 */
export const DEFAULT_PARAMS: AreaGeometriesQueryParams = {
  page: 1,
  pageSize: 100,
  active: true,
};

/**
 * The Areas spatial-export attribute catalog — the single source of truth shared by the Areas page's
 * Export Spatial Data picker (`areas-spatial-export.tsx`) and the Map Query "Areas" dataset's picker
 * (`pages/map-query/datasets/areas-utils.ts`), so the two surfaces can't drift. Ids match the backend
 * catalog `GisExport::Sources::AreaGeometry::AREA_COLUMNS`; labels mirror the Areas catalog columns.
 * Operator/State are intentionally absent — `area_geometries` carries no such field. Keep in sync with
 * `AREA_COLUMNS` when the catalog changes.
 */
export const AREA_EXPORT_ATTRIBUTES: { id: string; label: string }[] = [
  { id: 'display_name', label: 'Area Name' },
  { id: 'category', label: 'Area Type' },
  { id: 'shared', label: 'Shared' },
  { id: 'active', label: 'Active' },
  { id: 'data_source', label: 'Source' },
  { id: 'created_by', label: 'Created By' },
  { id: 'gross_acres', label: 'Gross Acres' },
  { id: 'created_at', label: 'Created At' },
  { id: 'updated_at', label: 'Updated At' },
];

/** Column id → date-range param. Only one date range is sent at a time. */
const DATE_COLUMNS = {
  createdAt: 'created_at',
  updatedAt: 'updated_at',
} as const;

type DateColumnId = keyof typeof DATE_COLUMNS;

const isDateColumn = (id: string): id is DateColumnId => id in DATE_COLUMNS;

/**
 * Translates the table's column-filter state into query params. Supported keys are
 * reset to `undefined` first, so clearing a filter drops it from the payload.
 *
 * @param {ColumnFiltersState} filters - TanStack column filter state.
 * @return {Partial<AreaGeometriesQueryParams>} The matching query params.
 */
export const columnFiltersToParams = (
  filters: ColumnFiltersState
): Partial<AreaGeometriesQueryParams> => {
  const params: Partial<AreaGeometriesQueryParams> = {
    category: undefined,
    data_source: undefined,
    created_at: undefined,
    updated_at: undefined,
    active: undefined,
    shared: undefined,
  };

  for (const filter of filters) {
    const { id, value } = filter;

    if (isDateColumn(id)) {
      const [start, end] = (value as (string | undefined)[]) ?? [];
      if (start || end) {
        params[DATE_COLUMNS[id]] = { start, end };
      }
      continue;
    }

    switch (id) {
      case 'category':
        params.category = firstOf(value);
        break;
      case 'dataSource':
        params.data_source = firstOf(value);
        break;
      case 'active': {
        // Two controls share this column — the Active checkbox and the Status select —
        // so a single `['true']`/`['false']` value drives both.
        const active = firstOf(value);
        params.active = active === undefined ? undefined : active === 'true';
        break;
      }
      case 'shared':
        params.shared =
          Array.isArray(value) && value.includes('true') ? true : undefined;
        break;
      default:
        break;
    }
  }

  return params;
};

/**
 * The catalog query takes one value per facet, while the multi-select filters
 * hand back an array. Takes the first entry so a multi-select degrades cleanly
 * to the single value the backend supports.
 *
 * @param {unknown} value - Raw filter value from the table.
 * @return {string | undefined} The first non-empty value, if any.
 */
function firstOf(value: unknown): string | undefined {
  if (Array.isArray(value)) {
    return (value as string[]).find(Boolean);
  }
  return (value as string) || undefined;
}
